import { RepairRequest, RepairStatus } from '../types/firestore';
import {
  getPrimaryClientUnreadSection,
  getSecondaryClientUnreadSection,
  normalizeClientUnreadSections,
  type ClientUnreadSection,
} from './clientUnread';
import {
  resolveServiceAssignment,
  type ServiceAssignmentResolution,
} from './repairRequestAssignment';
import {
  getRepairRequestOperationalIndexState,
  type RepairRequestOperationalIndexState,
} from '../shared/repairRequestDisplay';
import type { RepairRequestWorkflowActor } from './repairRequestWorkflow';
import {
  getPrimaryServiceUnreadSection,
  getSecondaryServiceUnreadSection,
  normalizeServiceUnreadSections,
  type ServiceUnreadSection,
} from './serviceUnread';

export type RepairRequestUnreadSection = 'messages' | 'quote' | 'status';
export type RepairRequestDetailSectionHint =
  | 'action'
  | 'messages'
  | 'description'
  | 'repair'
  | 'quote'
  | 'photos'
  | 'settlement'
  | 'technical'
  | 'review';

export const REPAIR_REQUEST_ASSIGNMENT_FIELDS = {
  canonicalServiceProviderId: 'serviceProviderId',
} as const;

export const REPAIR_REQUEST_UNREAD_SECTIONS: RepairRequestUnreadSection[] = [
  'messages',
  'quote',
  'status',
];

export const REPAIR_REQUEST_CLIENT_UNREAD_LEGACY_TYPES = [
  'message',
  'quote',
  'status_change',
] as const;

export const REPAIR_REQUEST_SERVICE_UNREAD_LEGACY_TYPES = [
  'message',
  'request_created',
  'quote_accepted',
  'status_change',
] as const;

export const REPAIR_REQUEST_UNREAD_FIELDS = {
  client: {
    ownerField: 'clientId',
    sectionsField: 'clientUnreadSections',
    legacyTypeField: 'clientUnreadActivityType',
    legacyTypes: REPAIR_REQUEST_CLIENT_UNREAD_LEGACY_TYPES,
  },
  service: {
    sectionsField: 'serviceUnreadSections',
    legacyTypeField: 'serviceUnreadActivityType',
    legacyTypes: REPAIR_REQUEST_SERVICE_UNREAD_LEGACY_TYPES,
  },
} as const;

export interface RepairRequestUnreadState {
  actor: RepairRequestWorkflowActor;
  sections: RepairRequestUnreadSection[];
  primarySection: RepairRequestUnreadSection | null;
  secondarySection: RepairRequestUnreadSection | null;
  hasUnreadMessages: boolean;
  hasFreshRequestActivity: boolean;
  detailSection: RepairRequestDetailSectionHint | null;
  hasCanonicalSections: boolean;
}

export interface RepairRequestReadModel {
  request: RepairRequest;
  requestId: string | null;
  assignment: ServiceAssignmentResolution;
  operationalIndex: RepairRequestOperationalIndexState;
  unread: {
    client: RepairRequestUnreadState;
    service: RepairRequestUnreadState;
  };
}

const normalizeIdentifier = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

export const getRepairRequestId = (
  request?: Pick<RepairRequest, 'id' | 'uid'> | null
): string | null => normalizeIdentifier(request?.id) || normalizeIdentifier(request?.uid);

export const getRepairRequestAssignment = (
  request?: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null
): ServiceAssignmentResolution => resolveServiceAssignment(request);

export const getAssignedServiceUserId = (
  request?: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null
): string | null => getRepairRequestAssignment(request).assignedServiceUserId;

export const isRepairRequestAssignedToServiceUser = (
  request: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null | undefined,
  userId?: string | null
): boolean => {
  const normalizedUserId = normalizeIdentifier(userId);
  if (!normalizedUserId) {
    return false;
  }
  return getAssignedServiceUserId(request) === normalizedUserId;
};

export const normalizeRepairRequestSectionHint = (
  section: unknown,
  options: { reviewRequested?: boolean } = {}
): RepairRequestDetailSectionHint | null => {
  const normalized = String(section || '')
    .trim()
    .toLowerCase();
  if (!normalized) {
    return options.reviewRequested ? 'review' : null;
  }
  if (
    normalized === 'messages' ||
    normalized === 'message' ||
    normalized === 'chat' ||
    normalized === 'communication' ||
    normalized === 'contact' ||
    normalized === 'komunikacja' ||
    normalized === 'kontakt'
  ) {
    return 'messages';
  }
  if (normalized === 'action' || normalized === 'actions' || normalized === 'cta') {
    return 'action';
  }
  if (normalized === 'description' || normalized === 'details' || normalized === 'opis') {
    return 'description';
  }
  if (
    normalized === 'repair' ||
    normalized === 'status' ||
    normalized === 'process' ||
    normalized === 'workflow' ||
    normalized === 'steps'
  ) {
    return 'repair';
  }
  if (normalized === 'quote' || normalized === 'wycena') {
    return 'quote';
  }
  if (normalized === 'photos' || normalized === 'photo' || normalized === 'foto') {
    return 'photos';
  }
  if (normalized === 'settlement' || normalized === 'billing' || normalized === 'payment') {
    return 'settlement';
  }
  if (normalized === 'technical' || normalized === 'print' || normalized === 'id') {
    return 'technical';
  }
  if (normalized === 'review') {
    return 'review';
  }
  return options.reviewRequested ? 'review' : null;
};

export const normalizeRepairRequestUnreadSectionToDetailSection = (
  section: RepairRequestUnreadSection | null
): RepairRequestDetailSectionHint | null => {
  if (section === 'messages') {
    return 'messages';
  }
  if (section === 'quote' || section === 'status') {
    return 'settlement';
  }
  return null;
};

const toMillis = (value: unknown): number | null => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.getTime() : null;
  }
  if (value && typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    const millis = (value as { toMillis: () => number }).toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    const millis = date.getTime();
    return Number.isFinite(millis) ? millis : null;
  }
  return null;
};

const isFreshServiceRequestUnread = (
  request: RepairRequest,
  sections: RepairRequestUnreadSection[]
): boolean => {
  if (!sections.includes('messages')) {
    return false;
  }

  const activityType = String(request.serviceUnreadActivityType || '')
    .trim()
    .toLowerCase();
  if (activityType === 'request_created') {
    return true;
  }

  if (activityType !== 'message' || request.status !== RepairStatus.NEW) {
    return false;
  }

  const createdAtMs = toMillis(request.createdAt);
  const lastActivityMs = toMillis(request.lastServiceActivityAt);
  if (createdAtMs === null || lastActivityMs === null) {
    return false;
  }

  return Math.abs(lastActivityMs - createdAtMs) <= 1000;
};

export const getRepairRequestUnreadState = (
  request: RepairRequest,
  actor: RepairRequestWorkflowActor
): RepairRequestUnreadState => {
  const rawSections =
    actor === 'service' ? request.serviceUnreadSections : request.clientUnreadSections;
  const hasCanonicalSections = Array.isArray(rawSections) && rawSections.length > 0;

  if (actor === 'service') {
    const sections = normalizeServiceUnreadSections(
      request.serviceUnreadSections,
      request.serviceUnreadActivityType || null
    ) as RepairRequestUnreadSection[];
    const primarySection = getPrimaryServiceUnreadSection(
      sections as ServiceUnreadSection[]
    ) as RepairRequestUnreadSection | null;
    const secondarySection = getSecondaryServiceUnreadSection(
      sections as ServiceUnreadSection[],
      primarySection as ServiceUnreadSection | null
    ) as RepairRequestUnreadSection | null;
    const hasFreshRequestActivity = isFreshServiceRequestUnread(request, sections);
    const hasUnreadMessages = sections.includes('messages') && !hasFreshRequestActivity;

    return {
      actor,
      sections,
      primarySection,
      secondarySection,
      hasUnreadMessages,
      hasFreshRequestActivity,
      detailSection: hasFreshRequestActivity
        ? 'description'
        : normalizeRepairRequestUnreadSectionToDetailSection(primarySection),
      hasCanonicalSections,
    };
  }

  const sections = normalizeClientUnreadSections(
    request.clientUnreadSections,
    request.clientUnreadActivityType || null
  ) as RepairRequestUnreadSection[];
  const primarySection = getPrimaryClientUnreadSection(
    sections as ClientUnreadSection[]
  ) as RepairRequestUnreadSection | null;
  const secondarySection = getSecondaryClientUnreadSection(
    sections as ClientUnreadSection[],
    primarySection as ClientUnreadSection | null
  ) as RepairRequestUnreadSection | null;

  return {
    actor,
    sections,
    primarySection,
    secondarySection,
    hasUnreadMessages: sections.includes('messages'),
    hasFreshRequestActivity: false,
    detailSection: normalizeRepairRequestUnreadSectionToDetailSection(primarySection),
    hasCanonicalSections,
  };
};

export const buildRepairRequestReadModel = (request: RepairRequest): RepairRequestReadModel => ({
  request,
  requestId: getRepairRequestId(request),
  assignment: getRepairRequestAssignment(request),
  operationalIndex: getRepairRequestOperationalIndexState(request),
  unread: {
    client: getRepairRequestUnreadState(request, 'client'),
    service: getRepairRequestUnreadState(request, 'service'),
  },
});

export const buildRepairRequestCardReadModel = buildRepairRequestReadModel;
export const buildRepairRequestOrderCardReadModel = buildRepairRequestReadModel;
